/**
 * Creating a channel, or starting a conversation.
 *
 * One dialog for both because the difference is small and real: a channel is
 * named and has a topic, a direct conversation is named by the people in it.
 * Whether that is `direct` or `group_direct` follows from how many people were
 * chosen, so the user never has to know the distinction exists.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, FormEvent, ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { isApiError } from '@/api/client'
import { useCreateRoom, useUsers } from '@/api/queries'
import type { CreateRoomRequest, RoomKind, RoomVisibility, User } from '@/api/types'
import {
  Avatar,
  Button,
  Checkbox,
  Modal,
  Spinner,
  TextArea,
  TextField,
  useToast,
} from '@/components/ui'
import { useExternalImages, userAvatarSrc } from '@/lib/externalImages'
import styles from './NewRoomDialog.module.css'

/** Duplicated rather than imported from the sidebar, which imports this file. */
function personName(user: User): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

const QUALIFIED_ID = /^[^@\s]+@[^@\s]+$/u

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

function HashGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M7.6 3.4 6 16.6M14 3.4l-1.6 13.2M3.5 7.6h13M2.8 12.4h13"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function LockGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 20 20" width="17" height="17" fill="none" aria-hidden="true" focusable="false">
      <rect x="4.4" y="9" width="11.2" height="7.6" rx="2.2" fill="currentColor" />
      <path d="M7.2 9V6.9a2.8 2.8 0 0 1 5.6 0V9" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )
}

function describeFailure(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return 'The room could not be created.'
}

function fieldMessage(error: unknown, field: string): string | null {
  if (!isApiError(error)) return null
  return error.fieldErrors[field]?.[0] ?? null
}

export interface NewRoomDialogProps {
  open: boolean
  /** `group_direct` is inferred from the number of people, never chosen. */
  kind: RoomKind
  onClose: () => void
  /** Fired after a successful create, once navigation has been requested. */
  onCreated?: (roomId: string) => void
}

export function NewRoomDialog({
  open,
  kind,
  onClose,
  onCreated,
}: NewRoomDialogProps): ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const { user, server } = useAuth()
  const createRoom = useCreateRoom()

  const isChannel = kind === 'channel'

  const [name, setName] = useState('')
  const [topic, setTopic] = useState('')
  const [visibility, setVisibility] = useState<RoomVisibility>('private')
  const [selected, setSelected] = useState<User[]>([])
  const [remote, setRemote] = useState<string[]>([])
  const [remoteDraft, setRemoteDraft] = useState('')
  const [remoteError, setRemoteError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [failure, setFailure] = useState<unknown>(null)

  const roomNameMaxLength = 80
  const topicMaxLength = 500

  const nameRef = useRef<HTMLInputElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Reopening must not show the previous attempt's half-filled form.
  useEffect(() => {
    if (!open) return
    setName('')
    setTopic('')
    setVisibility('private')
    setSelected([])
    setRemote([])
    setRemoteDraft('')
    setRemoteError(null)
    setQuery('')
    setSearch('')
    setFailure(null)
  }, [open, kind])

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 200)
    return () => window.clearTimeout(timer)
  }, [query])

  const { data: users, isLoading: loadingUsers } = useUsers(search, open)
  const allowExternalImages = useExternalImages()

  const candidates = useMemo(
    () => (users ?? []).filter((candidate) => candidate.id !== user?.id),
    [users, user],
  )

  const toggle = useCallback((candidate: User, checked: boolean): void => {
    setSelected((current) => {
      const without = current.filter((entry) => entry.id !== candidate.id)
      return checked ? [...without, candidate] : without
    })
  }, [])

  const addRemote = useCallback((): void => {
    const handle = remoteDraft.trim()
    if (handle === '') return
    if (!QUALIFIED_ID.test(handle)) {
      setRemoteError('Use the form name@server.')
      return
    }
    setRemoteError(null)
    setRemote((current) => (current.includes(handle) ? current : [...current, handle]))
    setRemoteDraft('')
  }, [remoteDraft])

  const participantCount = selected.length + remote.length
  const trimmedName = name.trim()

  const canSubmit = isChannel ? trimmedName !== '' : participantCount > 0

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      if (!canSubmit || createRoom.isPending) return

      const memberIds = selected.map((entry) => entry.id)
      const effectiveKind: RoomKind = isChannel
        ? 'channel'
        : participantCount > 1
          ? 'group_direct'
          : 'direct'

      const body: CreateRoomRequest = {
        kind: effectiveKind,
        member_ids: memberIds.length > 0 ? memberIds : undefined,
        qualified_ids: remote.length > 0 ? remote : undefined,
      }

      if (isChannel) {
        body.name = trimmedName
        body.visibility = visibility
        const trimmedTopic = topic.trim()
        if (trimmedTopic !== '') body.topic = trimmedTopic
      }

      setFailure(null)

      try {
        const room = await createRoom.mutateAsync(body)
        toast.success(isChannel ? `#${trimmedName} created.` : 'Conversation started.')
        navigate(`/rooms/${room.id}`)
        onCreated?.(room.id)
        onClose()
      } catch (error) {
        setFailure(error)
      }
    },
    [
      canSubmit,
      createRoom,
      isChannel,
      navigate,
      onClose,
      onCreated,
      participantCount,
      remote,
      selected,
      toast,
      topic,
      trimmedName,
      visibility,
    ],
  )

  const nameError = fieldMessage(failure, 'name')
  const generalError =
    failure === null ? null : nameError !== null ? null : describeFailure(failure)

  const federationOffered = server?.federation_enabled === true

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isChannel ? 'New channel' : 'New conversation'}
      description={
        isChannel
          ? 'Channels are for a subject or a project. They can be shared with a partner server later.'
          : 'Choose the people you want to talk to.'
      }
      closeOnOverlayClick={false}
      initialFocusRef={isChannel ? nameRef : searchRef}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form="new-room-form"
            loading={createRoom.isPending}
            disabled={!canSubmit}
          >
            {isChannel ? 'Create channel' : 'Start conversation'}
          </Button>
        </>
      }
    >
      <form id="new-room-form" className={styles.form} onSubmit={(event) => void submit(event)}>
        {generalError !== null ? (
          <p className={styles.error} role="alert">
            {generalError}
          </p>
        ) : null}

        {isChannel ? (
          <>
            <TextField
              ref={nameRef}
              label="Name"
              required
              value={name}
              error={nameError}
              placeholder="Room name"
              name="room-name"
              autoComplete="off"
              hint="Lower case, no spaces — for example prosjekt-x."
              maxLength={roomNameMaxLength}
              labelExtra={
                <span className={styles.characterCount} aria-live="polite">
                  {name.length}/{roomNameMaxLength}
                </span>
              }
              onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
            />
            <TextArea
              label="Topic"
              rows={2}
              value={topic}
              placeholder="Topic"
              name="room-topic"
              autoComplete="off"
              hint="What this channel is for. Shown in the header."
              maxLength={topicMaxLength}
              labelExtra={
                <span className={styles.characterCount} aria-live="polite">
                  {topic.length}/{topicMaxLength}
                </span>
              }
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setTopic(event.target.value)}
            />
            <fieldset className={styles.visibility}>
              <legend className={styles.visibilityLegend}>Who can join</legend>
              <button
                type="button"
                className={cx(
                  styles.visibilityCard,
                  visibility === 'private' && styles.visibilityCardSelected,
                )}
                aria-pressed={visibility === 'private'}
                onClick={() => setVisibility('private')}
              >
                <span className={styles.visibilityGlyph} aria-hidden="true">
                  <LockGlyph />
                </span>
                <span className={styles.visibilityCopy}>
                  <span className={styles.visibilityTitle}>Private</span>
                  <span className={styles.visibilityHint}>
                    People are added by invitation. The channel is hidden when others browse.
                  </span>
                </span>
                <span className={cx(styles.visibilityMark, visibility === 'private' && styles.visibilityMarkOn)} />
              </button>
              <button
                type="button"
                className={cx(
                  styles.visibilityCard,
                  visibility === 'public' && styles.visibilityCardSelected,
                )}
                aria-pressed={visibility === 'public'}
                onClick={() => setVisibility('public')}
              >
                <span className={styles.visibilityGlyph} aria-hidden="true">
                  <HashGlyph />
                </span>
                <span className={styles.visibilityCopy}>
                  <span className={styles.visibilityTitle}>Public</span>
                  <span className={styles.visibilityHint}>
                    Anyone in this Space can find the channel and join it themselves.
                  </span>
                </span>
                <span className={cx(styles.visibilityMark, visibility === 'public' && styles.visibilityMarkOn)} />
              </button>
            </fieldset>

            <div>
              <p className={styles.previewLabel}>How it looks in the list</p>
              <div className={styles.previewRow}>
                <span className={styles.previewGlyph} aria-hidden="true">
                  {visibility === 'private' ? <LockGlyph /> : <HashGlyph />}
                </span>
                <span className={styles.previewName}>
                  {visibility === 'private'
                    ? `#${trimmedName === '' ? 'name' : trimmedName}`
                    : trimmedName === ''
                      ? 'name'
                      : trimmedName}
                </span>
              </div>
            </div>
          </>
        ) : null}

        {selected.length > 0 || remote.length > 0 ? (
          <ul className={styles.chips} aria-label="Chosen participants">
            {selected.map((entry) => (
              <li key={entry.id} className={styles.chip}>
                <Avatar name={personName(entry)} id={entry.id} src={userAvatarSrc(entry, allowExternalImages)} size="xs" decorative />
                {personName(entry)}
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`Remove ${personName(entry)}`}
                  onClick={() => toggle(entry, false)}
                >
                  ×
                </button>
              </li>
            ))}
            {remote.map((handle) => (
              <li key={handle} className={styles.chip}>
                {handle}
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`Remove ${handle}`}
                  onClick={() => setRemote((current) => current.filter((entry) => entry !== handle))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div>
          <TextField
            ref={searchRef}
            label={isChannel ? 'Add people' : 'People'}
            type="search"
            value={query}
            placeholder="Search by name or username"
            autoComplete="off"
            onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          />

          {loadingUsers ? (
            <p className={styles.status}>
              <Spinner size="sm" label="Searching" />
            </p>
          ) : candidates.length === 0 ? (
            <p className={styles.status}>No one matched.</p>
          ) : (
            <ul className={styles.people}>
              {candidates.map((candidate) => {
                const chosen = selected.some((entry) => entry.id === candidate.id)
                return (
                  <li key={candidate.id} className={styles.person}>
                    <Checkbox
                      checked={chosen}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        toggle(candidate, event.target.checked)
                      }
                      label={
                        <span className={styles.personLabel}>
                          <Avatar
                            name={personName(candidate)}
                            id={candidate.id}
                            src={userAvatarSrc(candidate, allowExternalImages)}
                            size="xs"
                            decorative
                          />
                          <span className={styles.personText}>
                            <span className={styles.personName}>{personName(candidate)}</span>
                            <span className={styles.personHandle}>{candidate.qualified_id}</span>
                          </span>
                        </span>
                      }
                    />
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {federationOffered ? (
          <details className={styles.federationDetails}>
            <summary className={styles.federationSummary}>Add someone from another server</summary>
            <div className={styles.federationPanel}>
              <p className={styles.federationHint}>
                Optional. You can also invite federated members after the channel is created.
              </p>
              <div className={styles.remoteRow}>
                <TextField
                  className={styles.remoteField}
                  label="Someone on another server"
                  value={remoteDraft}
                  placeholder="name@partner.example"
                  error={remoteError}
                  autoComplete="off"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setRemoteDraft(event.target.value)
                    setRemoteError(null)
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return
                    // Otherwise Enter would submit the form with the handle unadded.
                    event.preventDefault()
                    addRemote()
                  }}
                />
                <Button onClick={addRemote} disabled={remoteDraft.trim() === ''}>
                  Add
                </Button>
              </div>
            </div>
          </details>
        ) : null}
      </form>
    </Modal>
  )
}
